import dayjs from 'dayjs'
import { lookupClientData } from './getClientDataFromApi';
import _ from "lodash"
import { formatter } from './formatter';

/** ==============================================================================
 *  When we are parsing content to appear in the template, we often need to parse
 *  placeholder values . All parsing of template content happens here.
 * 
 *  This function requires:
 *  
 *  - The placeholder
 *  - the data obj which has the real data values (dataObj)
 *  - the global api data (apiData)
 * 
 *  Placeholders will usually be surrounded by double square brackets, like this:
 *      [[placeholder]]
 *  or single curly braces, like this:
 *    { placeholder } 
 * 
 *  Inside the placeholder we can also specify a modifier, like this:
 *   [[placeholder::modifier]]
 * 
 * ================================================================================ */

interface ParseContentProps {
  content:string
  clientDataObj:any 
  apiData:any,
  special?:any
}


function applyFormatting(path:string, clientDataObj:any, apiData:any, idx:number = 0, modifier = null, special:any = null) {
  // const fixedKey = path.replace(/\.(\w+)\[\d+\]/g, '.$1{Object}')
  const apiLookupPath = path.replace(/\[idx\]/g, "{Object}");
  const dataLookupPath = path.replace(/\[idx\]/g, `[${idx}]`);
  // console.log(path)
  // console.log(clientDataObj)

  // console.log(`applyFormatting: ${key}|${fixedKey}`)
  const format = apiData.find( (item:any) => item.path === apiLookupPath )?.format

  // console.log(`format: ${format}`)
  const lookupType = (apiLookupPath.startsWith("rp.") ||  apiLookupPath.startsWith("merchant.") ||  apiLookupPath.startsWith("merch.")) ? "path" : "placeholder"

  let clientData
  try {
    const { result, report:apiReport } = lookupClientData({ clientDataObj, apiData, lookupType, lookupValue: dataLookupPath }) 
    clientData = result
  } catch (error) {
    console.error(`Error getting value for key "${path}": ${error}`)
  }
    
  if (!clientData) {
    // console.log(`No value found for key "${key}".`) // If no value is found, return the original placeholder 
    return ""
  }

  // the special object is used for rules-based formatting, and supercedes any other instrcutions
  else if( special && special[path] ) {
    try {
      clientData = applyFormatter(clientData, special[path]) // Apply the special formatting if present
    } catch (error) {
      console.error(`Error applying special format "${special[path]}" to value "${clientData}": ${error}`)
    }
  }

  // a modifier overrides any default format specified in the apidata object.
  else if( clientData && modifier ) {
    try {
      clientData = applyModifier(clientData, modifier) // Apply the modifier if present
    } catch (error) {
      console.error(`Error applying modifier "${modifier}" to value "${clientData}": ${error}`)
    }
      
  } else if( clientData && format && format.length ) {
    try {
      clientData = formatter.matchApiFormat(clientData, format) // Apply the modifier if present
    } catch (error) { 
      console.error(`Error applying format "${format}" to clientData "${clientData}": ${error}`)
    }
  }

  return clientData
}




/** Parses content that has only one placeholder (summary table, trx history  table, etc) */
export function parseSingleContent({ path, clientDataObj, apiData, idx = 0, modifier=null, special = null }) {
  // console.log(`parseSingleContent: ${content}`)
  // const result = _.get(clientDataObj, content, "")
  const clientData:any = applyFormatting(path, clientDataObj, apiData, idx, modifier, special)
  return clientData
}


/** Parses content that may contain multiple placeholders (opening statements, etc) */
export function parseContent({ content, clientDataObj, apiData, special = null }:ParseContentProps) {
  let report = ""
  const regex = /(\[\[\s*(.*?)\s*(?:::(.*?))?\s*\]\]|\{\s*(.*?)\s*(?:::(.*?))?\s*\})/g;

  // console.log(`content to evaluate: ${content}`)

  const result = content.replace(regex, (match, _, key1, modifier1, key2, modifier2) => {
    const key = key1 || key2;  // Get the main placeholder name
    const modifier = modifier1 || modifier2; // Get the modifier if present

    // const lookupType = (key.startsWith("rp.") ||  key.startsWith("merchant.") ||  key.startsWith("merch.")) ? "path" : "placeholder"

    // look up the placeholder in apiData and see if any format has been specified 
    // const format = apiData.find( (item:any) => item.path === key )?.format

    const clientData:any = applyFormatting(key, clientDataObj, apiData, 0, modifier, special)
    return clientData
  })

  // console.log(result) 
  return { result, report } 
}


export function parseContentGroup({ group, clientDataObj, apiData }:any) {
  let results = {}
  group.forEach( (contentObj:any) => {
    const { result } = parseContent({ content: contentObj.content, clientDataObj, apiData })
    results = {...results, [contentObj.key]: result}
  })
  return results
}


export function applyFormatter(value:any, format:string):any {
  if( !format ) return value
  const formatType = format.split("|")[0]
  const formatConfig = format.split("|")[1]

  // console.log(`applyFormatter: ${value}`)
  // console.log(`formatType: ${formatType}`)
  // console.log(`formatConfig: ${formatConfig}`)

  if( formatType === "date" ) {
    const dateFormat = formatConfig || "MMM DD, YYYY"
    value = dayjs(value).format(dateFormat)
  } else if (formatType === "number"){
    if( formatConfig === "float" ){
      value = parseFloat(value).toFixed(2)
    } else if( formatConfig === "int" ){
      value = parseInt(value)
    } else if( formatConfig === "usd" ){
      value = `$${parseFloat(value).toFixed(2)}`
    } else if( formatConfig === "euro" ){
      value = `€${parseFloat(value).toFixed(2)}`
    } else if( formatConfig === "percent" ){
      value = `${parseFloat(value).toFixed(2)}%`
    } else if( formatConfig === "capitalize" ){
      value = value.replace(/\b\w/g, (char:any) => char.toUpperCase())
    } else if( formatConfig === "lowercase" ){
      value = value.toLowerCase()
    } else {
      console.warn(`Unknown format config: ${formatConfig}`)
    }
  }
  return value
}


export function applyModifier(value:any, modifier:string):any {
  if( !modifier ) return value

  switch (modifier.toLowerCase()) {
    case "uppercase":
      value = value.toUpperCase();
      break;
    case "lowercase":
      value = value.toLowerCase();
      break;
    case "count":
      value = Array.isArray(value) ? value.length : "N/A";
      break;
    case "date":
      const dateVal = dayjs(value).format("MMM DD, YYYY");
      value = dateVal; 
      break;
    case "usd":
      value = `$${parseFloat(value).toFixed(2)}`;
      break;
    default:
      console.warn(`Unknown modifier: ${modifier}`);
  }

  return value;
}